/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMsal } from '@azure/msal-react';
import {
    Button,
    Dialog,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    Drawer,
    DrawerBody,
    DrawerHeader,
    DrawerHeaderTitle,
    Field,
    Input,
    makeStyles,
    Spinner,
    Table,
    TableBody,
    TableCell,
    TableCellLayout,
    TableHeader,
    TableHeaderCell,
    TableRow,
    tokens
} from '@fluentui/react-components';
import { Add24Regular, Dismiss24Filled, Dismiss24Regular, Edit24Regular } from '@fluentui/react-icons';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { IDataItem } from '../../models/IDataItem';
import { DataService } from '../../services/dataService';

type SortDirection = 'asc' | 'desc' | null;
type SortColumn = 'value' | 'text' | null;


interface DrawerState {
    isOpen: boolean;
    item: IDataItem | null;
    isEditMode: boolean;
}

const useStyles = makeStyles({
    container: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        padding: tokens.spacingHorizontalL,
        gap: tokens.spacingVerticalL,
    },
    headerContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: tokens.spacingHorizontalL,
    },
    filterContainer: {
        display: 'flex',
        gap: tokens.spacingHorizontalM,
        alignItems: 'center',
        flex: 1,
    },
    searchInput: {
        flex: 1,
        maxWidth: '400px',
    },
    loadingContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '60px 20px',
        minHeight: '300px',
    },
    emptyState: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        minHeight: '100px',
        color: tokens.colorNeutralForeground3,
        fontSize: tokens.fontSizeBase300,
        backgroundColor: tokens.colorNeutralBackground2,
        borderRadius: tokens.borderRadiusMedium,
        textAlign: 'center',
        margin: '10px',
    },
    tableContainer: {
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
        flex: 1,
        border: `1px solid ${tokens.colorNeutralStroke1}`,
        borderRadius: tokens.borderRadiusMedium,
    },
    tableRow: {
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: tokens.colorNeutralBackground1Hover,
        },
    },
    actionButtons: {
        display: 'flex',
        gap: tokens.spacingHorizontalXS,
    },
    colActions: {
        width: '100px',
        minWidth: '100px',
    },
    colCode: {
        width: '120px',
        minWidth: '100px',
    },
    colName: {
        width: '60%',
        minWidth: '200px',
    },
    colStatus: {
        width: '20%',
        minWidth: '100px',
    },
    // Drawer styles
    section: {
        marginBottom: tokens.spacingVerticalL,
    },
    sectionTitle: {
        fontSize: tokens.fontSizeBase500,
        fontWeight: 700,
        marginBottom: tokens.spacingVerticalM,
        color: tokens.colorNeutralForeground1,
    },
    formGroup: {
        display: 'grid',
        gap: tokens.spacingVerticalM,
        marginBottom: tokens.spacingVerticalL,
    },
    fieldRow: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: tokens.spacingHorizontalM,
    },
    fullWidth: {
        gridColumn: '1 / -1',
    },
    switchField: {
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalM,
    },
    footerActions: {
        display: 'flex',
        gap: tokens.spacingHorizontalM,
        justifyContent: 'flex-end',
        paddingTop: tokens.spacingVerticalL,
        borderTop: `1px solid ${tokens.colorNeutralStroke1}`,
    },
});

const SourceOfFundsTab: React.FC = () => {
    const styles = useStyles();
    const { instance, accounts } = useMsal();

    const [items, setItems] = useState<IDataItem[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [filterText, setFilterText] = useState('');
    const [sortColumn, setSortColumn] = useState<SortColumn>('value');
    const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
    const [drawerState, setDrawerState] = useState<DrawerState>({
        isOpen: false,
        item: null,
        isEditMode: false,
    });
    const [formData, setFormData] = useState<IDataItem>({
        key: '',
        value: '',
        text: '',
    });
    const [isSaving, setIsSaving] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<IDataItem | null>(null);

    const dataService = React.useMemo(() => {
        const accountInfo = accounts[0] || null;
        return accountInfo ? new DataService(instance, accountInfo) : null;
    }, [instance, accounts]);

    // Fetch all items
    useEffect(() => {
        const fetchItems = async () => {
            if (!dataService) {
                return;
            }

            try {
                setIsLoading(true);
                const data = await dataService.getMittelherkunftsItems();
                console.log('Fetched source of funds items:', data);
                setItems(data as any || []);
            } catch (err) {
                console.error('Error loading source of funds items:', err);
                setItems([]);
            } finally {
                setIsLoading(false);
            }
        };

        fetchItems();
    }, [dataService]);

    // Filter items based on search text
    const filteredItems = useMemo(() => {
        let results = items;

        // Apply search filter
        if (filterText.trim()) {
            const searchLower = filterText.toLowerCase();
            results = results.filter((item) => {
                return (
                    item.text?.toLowerCase().includes(searchLower)
                );
            });
        }

        // Apply sorting
        if (sortColumn) {
            results = [...results].sort((a, b) => {
                let aValue: any = a[sortColumn];
                let bValue: any = b[sortColumn];

                // Handle null/undefined
                if (aValue == null) aValue = '';
                if (bValue == null) bValue = '';

                // Convert to lowercase for string comparison
                if (typeof aValue === 'string') aValue = aValue.toLowerCase();
                if (typeof bValue === 'string') bValue = bValue.toLowerCase();

                // Comparison
                if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
                if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
                return 0;
            });
        }

        return results;
    }, [items, filterText, sortColumn, sortDirection]);

    const handleSort = useCallback((column: SortColumn) => {
        if (sortColumn === column) {
            // Toggle direction or clear sort
            if (sortDirection === 'asc') {
                setSortDirection('desc');
            } else if (sortDirection === 'desc') {
                setSortColumn('text');
                setSortDirection('asc');
            }
        } else {
            setSortColumn(column);
            setSortDirection('asc');
        }
    }, [sortColumn, sortDirection]);

    const handleAddNew = () => {
        setFormData({
            key: '',
            value: '',
            text: ''
        });
        setDrawerState({
            isOpen: true,
            item: null,
            isEditMode: false,
        });
    };

    const handleView = (item: IDataItem) => {
        setFormData(item);
        setDrawerState({
            isOpen: true,
            item: item,
            isEditMode: false,
        });
    };

    const handleEdit = (item: IDataItem) => {
        setFormData(item);
        setDrawerState({
            isOpen: true,
            item: item,
            isEditMode: true,
        });
    };

    const handleRowDoubleClick = (item: IDataItem) => {
        handleView(item);
    };

    const handleDrawerClose = () => {
        setDrawerState({
            isOpen: false,
            item: null,
            isEditMode: false,
        });
        setFormData({
            key: '',
            value: '',
            text: ''
        });
    };

    const handleDrawerSave = async () => {
        if (!dataService) return;

        if (!formData.text?.trim()) {
            alert('Please enter a name for the source of funds');
            return;
        }

        try {
            setIsSaving(true);

            if (drawerState.isEditMode && drawerState.item) {
                // Update existing
                await dataService.updateMittelherkunft(parseInt(formData.value), formData.text);
                setItems((prevItems) =>
                    prevItems.map((p) => (p.value === formData.value ? formData : p))
                );
            } else {
                // Add new
                await dataService.addMittelherkunft(parseInt(formData.value), formData.text);
                // Reload the list
                const data = await dataService.getMittelherkunftsItems();
                setItems(data as any || []);
            }

            handleDrawerClose();
        } catch (err) {
            console.error('Error saving source of funds:', err);
            alert('Error saving source of funds: ' + (err instanceof Error ? err.message : 'Unknown error'));
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteClick = (item: IDataItem) => {
        setItemToDelete(item);
        setShowDeleteDialog(true);
    };

    const handleConfirmDelete = async () => {
        if (!dataService || !itemToDelete) return;

        try {
            setIsSaving(true);
            await dataService.deleteMittelherkunft(parseInt(itemToDelete.value));

            // Remove the item from the list
            setItems((prevItems) =>
                prevItems.filter((p) => p.value !== itemToDelete.value)
            );

            setShowDeleteDialog(false);
            setItemToDelete(null);
        } catch (err) {
            console.error('Error deleting source of funds:', err);
            alert('Error deleting source of funds: ' + (err instanceof Error ? err.message : 'Unknown error'));
        } finally {
            setIsSaving(false);
        }
    };

    // Check if ID is already in use
    const isIdAlreadyInUse = useCallback(() => {
        // Only check when adding new (not in edit mode)
        if (drawerState.isEditMode || drawerState.item) {
            return false;
        }

        // Check if the entered ID already exists in items
        if (formData.value) {
            return items.some((item) => item.value === formData.value);
        }

        return false;
    }, [formData, drawerState, items]);

    const isFormValid = useCallback(() => {
        // Name is always required
        if (!formData.text || formData.text.trim() === '') {
            return false;
        }

        // If adding new (not in edit mode), ID/Value is also required
        if (!drawerState.isEditMode && !drawerState.item) {
            if (!formData.value || formData.value.toString().trim() === '') {
                return false;
            }

            // Check if ID is already in use
            if (isIdAlreadyInUse()) {
                return false;
            }
        }

        return true;
    }, [formData, drawerState, isIdAlreadyInUse]);

    return (
        <div className={styles.container}>
            {/* Header with Filter and Add Button */}
            <div className={styles.headerContainer}>
                <div className={styles.filterContainer}>
                    <Input
                        placeholder="Search by name..."
                        value={filterText}
                        onChange={(_, data) => setFilterText(data.value)}
                        className={styles.searchInput}
                    />
                </div>
                <Button
                    icon={<Add24Regular />}
                    appearance="primary"
                    onClick={handleAddNew}
                >
                    Add Source of Funds
                </Button>
            </div>

            {/* Loading State */}
            {isLoading && (
                <div className={styles.loadingContainer}>
                    <Spinner label="Loading source of funds..." />
                </div>
            )}

            {/* Empty State */}
            {!isLoading && items.length === 0 && (
                <div className={styles.emptyState}>
                    No source of funds found. Click "Add Source of Funds" to create one.
                </div>
            )}

            {/* Items Table */}
            {!isLoading && filteredItems.length > 0 && (
                <div className={styles.tableContainer}>
                    <Table size="small">
                        <TableHeader>
                            <TableRow>
                                <TableHeaderCell className={styles.colActions}>
                                    Actions
                                </TableHeaderCell>
                                <TableHeaderCell
                                    className={styles.colCode}
                                    onClick={() => handleSort('value')}
                                >
                                    ID
                                </TableHeaderCell>
                                <TableHeaderCell
                                    className={styles.colName}
                                    onClick={() => handleSort('text')}
                                >
                                    Name
                                </TableHeaderCell>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filteredItems.map((item) => (
                                <TableRow
                                    key={item.value}
                                    className={styles.tableRow}
                                    onDoubleClick={() => handleRowDoubleClick(item)}
                                >
                                    <TableCell>
                                        <TableCellLayout>
                                            <div className={styles.actionButtons}>
                                                <Button
                                                    icon={<Edit24Regular />}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleEdit(item);
                                                    }}
                                                    appearance="subtle"
                                                    size="small"
                                                    title="Edit"
                                                />
                                                <Button
                                                    icon={<Dismiss24Filled />}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDeleteClick(item);
                                                    }}
                                                    appearance="subtle"
                                                    size="small"
                                                    title="Delete"
                                                />
                                            </div>
                                        </TableCellLayout>
                                    </TableCell>
                                    <TableCell>
                                        <TableCellLayout>{item.value || '-'}</TableCellLayout>
                                    </TableCell>
                                    <TableCell>
                                        <TableCellLayout>{item.text || '-'}</TableCellLayout>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )}

            {/* No results after filtering */}
            {!isLoading && items.length > 0 && filteredItems.length === 0 && (
                <div className={styles.emptyState}>
                    No source of funds match your search
                </div>
            )}

            {/* Drawer for Add/Edit */}
            <Drawer
                open={drawerState.isOpen}
                onOpenChange={(_, { open }) => {
                    if (!open) handleDrawerClose();
                }}
                size="medium"
                type="overlay"
                position="end"
            >
                <DrawerHeader>
                    <DrawerHeaderTitle
                        action={
                            <Button
                                appearance="subtle"
                                aria-label="Close"
                                icon={<Dismiss24Regular />}
                                onClick={handleDrawerClose}
                            />
                        }
                    >
                        {drawerState.isEditMode ? 'Edit Source of Funds' : drawerState.item ? 'View Source of Funds' : 'Add Source of Funds'}
                    </DrawerHeaderTitle>
                </DrawerHeader>

                <DrawerBody>
                    <div className={styles.section}>
                        <div className={styles.formGroup}>
                            <Field label="ID" required>
                                <Input
                                    value={formData.value || ''}
                                    onChange={(_, data) =>
                                        setFormData({ ...formData, value: data.value, key: data.value })
                                    }
                                    disabled={drawerState.isEditMode || (!drawerState.isEditMode && drawerState.item !== null)}
                                    placeholder=""
                                />
                            </Field>
                            {isIdAlreadyInUse() && (
                                <div style={{
                                    color: tokens.colorPaletteRedForeground1,
                                    fontSize: tokens.fontSizeBase200,
                                    marginTop: tokens.spacingVerticalXS,
                                    marginBottom: tokens.spacingVerticalM,
                                }}>
                                    ⚠️ This ID is already in use
                                </div>
                            )}
                            <Field label="Name" required>
                                <Input
                                    value={formData.text || ''}
                                    onChange={(_, data) =>
                                        setFormData({ ...formData, text: data.value })
                                    }
                                    disabled={!drawerState.isEditMode && drawerState.item !== null}
                                    placeholder="Enter source of funds name"
                                />
                            </Field>
                        </div>

                        {/* Footer Actions */}
                        <div className={styles.footerActions}>
                            <Button
                                appearance="secondary"
                                onClick={handleDrawerClose}
                                disabled={isSaving}
                            >
                                {drawerState.isEditMode || !drawerState.item ? 'Cancel' : 'Close'}
                            </Button>
                            {(drawerState.isEditMode || !drawerState.item) && (
                                <Button
                                    appearance="primary"
                                    onClick={handleDrawerSave}
                                    disabled={isSaving || !isFormValid()}
                                >
                                    {isSaving ? <Spinner size="tiny" /> : 'Save'}
                                </Button>
                            )}
                        </div>
                    </div>
                </DrawerBody>
            </Drawer>

            {/* Delete Confirmation Dialog */}
            <Dialog open={showDeleteDialog} onOpenChange={(_, data) => setShowDeleteDialog(data.open)}>
                <DialogSurface>
                    <DialogBody>
                        <DialogTitle>
                            Delete Source of Funds
                        </DialogTitle>
                        <DialogContent>
                            Are you sure you want to delete{' '}
                            <strong>{itemToDelete?.text}</strong>?
                        </DialogContent>
                        <div
                            style={{
                                display: 'flex',
                                gap: tokens.spacingHorizontalM,
                                justifyContent: 'flex-end',
                                paddingTop: tokens.spacingVerticalL,
                            }}
                        >
                            <Button
                                appearance="secondary"
                                onClick={() => setShowDeleteDialog(false)}
                                disabled={isSaving}
                            >
                                Cancel
                            </Button>
                            <Button
                                appearance="primary"
                                onClick={handleConfirmDelete}
                                disabled={isSaving}
                            >
                                {isSaving ? <Spinner size="tiny" /> : 'Delete'}
                            </Button>
                        </div>
                    </DialogBody>
                </DialogSurface>
            </Dialog>
        </div>
    );
};

export default SourceOfFundsTab;
